import { useState } from 'react';
import { Share2, X, MessageCircle, Link2, Copy, Check, Send, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ShareButtonProps {
  title: string;
  text: string;
  url: string;
  className?: string;
}

export function ShareButton({ title, text, url, className }: ShareButtonProps) {
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    // Try native share first
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text,
          url,
        });
        return;
      } catch (err) {
        // User cancelled or error, show custom modal
        console.log('Native share cancelled or failed:', err);
      }
    }
    // Show custom share modal
    setShowModal(true);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareViaWhatsApp = () => {
    const message = encodeURIComponent(`${text}\n${url}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
    setShowModal(false);
  };

  const shareViaFacebook = () => {
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowModal(false);
  };

  const shareViaMessenger = () => {
    const shareUrl = `https://www.facebook.com/dialog/send?link=${encodeURIComponent(url)}&app_id=123456789&redirect_uri=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowModal(false);
  };

  const shareViaInstagram = () => {
    // Instagram doesn't have a direct web share, copy to clipboard
    copyLink();
    alert('Link copiado! Cola no Instagram para partilhar.');
    setShowModal(false);
  };

  const shareViaTwitter = () => {
    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'width=600,height=400');
    setShowModal(false);
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent(title);
    const body = encodeURIComponent(`${text}\n\n${url}`);
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
    setShowModal(false);
  };

  const shareViaSMS = () => {
    const message = encodeURIComponent(`${text} ${url}`);
    window.open(`sms:?body=${message}`, '_blank');
    setShowModal(false);
  };

  const shareOptions = [
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: MessageCircle,
      color: 'bg-green-500',
      onClick: shareViaWhatsApp,
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: () => (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      color: 'bg-blue-600',
      onClick: shareViaFacebook,
    },
    {
      id: 'messenger',
      name: 'Messenger',
      icon: () => (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0C5.373 0 0 4.975 0 11.111c0 3.497 1.744 6.616 4.472 8.652V24l4.086-2.242c1.09.301 2.246.464 3.442.464 6.627 0 12-4.975 12-11.111C24 4.975 18.627 0 12 0zm1.193 14.963l-3.056-3.26-5.963 3.26 6.559-6.963 3.13 3.26 5.889-3.26-6.559 6.963z"/>
        </svg>
      ),
      color: 'bg-gradient-to-br from-blue-500 to-purple-500',
      onClick: shareViaMessenger,
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: () => (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
        </svg>
      ),
      color: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500',
      onClick: shareViaInstagram,
    },
    {
      id: 'twitter',
      name: 'X (Twitter)',
      icon: () => (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      color: 'bg-black',
      onClick: shareViaTwitter,
    },
    {
      id: 'email',
      name: 'Email',
      icon: Mail,
      color: 'bg-gray-500',
      onClick: shareViaEmail,
    },
    {
      id: 'sms',
      name: 'SMS',
      icon: Send,
      color: 'bg-blue-400',
      onClick: shareViaSMS,
    },
  ];

  return (
    <>
      <button
        onClick={handleShare}
        className={cn(
          'p-2 rounded-xl hover:bg-muted transition-colors',
          className
        )}
        aria-label="Partilhar perfil"
      >
        <Share2 className="w-5 h-5 text-muted-foreground" />
      </button>

      {/* Share Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setShowModal(false)}
        >
          <div 
            className="bg-white w-full max-w-md sm:rounded-3xl rounded-t-3xl p-6 animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-foreground">Partilhar</h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 rounded-full hover:bg-muted transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Share Options Grid */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              {shareOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.id}
                    onClick={option.onClick}
                    className="flex flex-col items-center gap-2 group"
                  >
                    <div className={cn(
                      'w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg transition-transform group-hover:scale-110',
                      option.color
                    )}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className="text-xs text-muted-foreground">{option.name}</span>
                  </button>
                );
              })}
            </div>

            {/* Copy Link */}
            <div className="bg-kuiva-light rounded-xl p-4">
              <p className="text-xs text-muted-foreground mb-2">Ou copia o link</p>
              <div className="flex items-center gap-3">
                <div className="flex-1 flex items-center gap-2 bg-white rounded-lg px-3 py-2">
                  <Link2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <p className="text-sm text-foreground truncate">{url}</p>
                </div>
                <button
                  onClick={copyLink}
                  className="flex items-center gap-1.5 px-4 py-2 bg-kuiva-blue text-white rounded-lg font-medium hover:bg-kuiva-blue/90 transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copiado
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copiar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
